import { Feedback } from "@mui/icons-material"
import { Box, Grid, TextField, Typography } from "@mui/material"
import { customerHasFeature } from "auth/customerHasFeature"
import { hasRole } from "auth/hasRole"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import Button from "components/Button"
import ClpTable from "components/ClpTable"
import { MultiSelectFilter } from "components/ClpTable/ClpTableFilterTypes"
import PaperBox from "components/PaperBox"
import { TabPanel } from "components/navigation/TabPanel"
import { Formik, useFormikContext } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { allFeatures } from "generated/features"
import { allRoles } from "generated/roles"
import { departmentsWithListId } from "pages/customer/pages/settings/pages/companies/components/CompanyForm"
import { removeQueryFromImgSrc } from "pages/documents/helpers/imageSrcSasKey"
import { companyValidationSchema } from "pages/systemadmin/pages/customers/components/validationSchema"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import BplIntegrationSwitch from "./BplIntegrationSwitch"
import DepartmentForm from "./DepartmentForm"
import ViolationSwitch from "./ViolationSwitch"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import CrisisInfoForm from "shared/customer/CrisisInfo/CrisisInfoForm"
import IssueReportNotification from "components/SettingsSwitch/IssuereportNotification/IssuereportNotification"
import VtrSwitch from "components/SettingsSwitch/VtrSwitch"
import DepartmentsList from "shared/customer/DepartmentList/DepartmentsList"
import { useTranslation } from "react-i18next"


function companyInitialValues(company) {
  if (company) {
    company.bplIntegrationActive = company?.bplUrl !== null
  }
  return company
    ? company
    : {
        id: 0,
        name: "",
        organisationNumber: "",
        streetAddress: "",
        postalCode: "",
        cityAddress: "",
        hasVtrIntegration: false,
        bplIntegrationActive: false,
        violationRegulations: []
      }
}

export const CompanyForm = ({
  customer,
  company,
  onSubmit,
  result,
  openRemoveDialog,
  tabValue,
  setCurrentWorkshop,
}) => {
  const appContext = useContext(AppContext)
  const { t } = useTranslation()
  const formRef = useRef()
  const [getCrisisInfoResult, getCrisisInfoApi] = useApi()
  const [upsertCrisisInfoResult, upsertCrisisInfoApi] = useApi()
  const [workshopsResults, workshopsApi] = useApi()

  useEffect(() => {
    if (tabValue === 3) {
      const url = `${apiEndpoints.vehicleregistrychecks.getcompanycarworkshops}?companyId=${company.id}`
      workshopsApi.get(url)
    }
  }, [tabValue])

  const [departments, setDepartments] = useState(
    company ? departmentsWithListId(company.departments) : []
  )
  const editMode = company ? true : false
  const initialValues = companyInitialValues(company)

  const handleSubmit = async (values, { resetForm }) => {
    const customerId = customer.id
    const companyToCreate = {
      ...values,
      customerId: customerId,
      departments: departments
    }

    onSubmit(companyToCreate)
  }

  const workshopInitialFilters = [
    {
      id: "isActive",
      value: "yes"
    }
  ]

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: t("global.workshops", "Workshops")
      },
      {
        accessorKey: "emailAddress",
        header: t("global.email", "Email")
      },
      {
        accessorFn: (row) => {
          return row.isActive ? "yes" : "no"
        },
        cell: ({ row }) => {
          const label = row.original.isActive ? (
            <span>{t("global.yes", "Yes")}</span>
          ) : (
            <span>{t("global.no", "No")}</span>
          )
          return <>{label}</>
        },
        header: t("global.active", "Active"),
        filterFn: "arrIncludesSome",
        meta: {
          mainFilter: true,
          defaultValue: ["yes"],
          filter: MultiSelectFilter,
          filterOptions: [
            { value: "yes", name: t("global.yes", "Yes") },
            { value: "no", name: t("global.no", "No") }
          ]
        }
      }
    ],
    []
  )

  useEffect(() => {
    if (hasRole(appContext.appUser, allRoles.VehicleRegistryAdmin)) {
      columns.push({
        accessorKey: "edit",
        header: "",
        meta: {
          align: "right"
        },

        cell: ({ cell }) => {
          return (
            <TableButton
              type="edit"
              handleOnClick={() => {
                openSidebar(cell.row.original)
              }}
            />
          )
        }
      })
    }
  }, [])

  useEffect(() => {
    if (!company) return
    getCrisisInfoApi.get(
      apiEndpoints.crisismanagement.getcrisisinfobycompanyid.replace("{companyId}", company.id)
    )
  }, [getCrisisInfoApi])

  function openSidebar(workshop) {
    setCurrentWorkshop(workshop)
    appContext.toggleSideBar(true)
  }
  const [validationStep, setValidationStep] = useState(companyValidationSchema(t))

  const handleCrisisSubmit = (formikValues) => {
    if (formikValues?.body) {
      formikValues.body = removeQueryFromImgSrc(formikValues.body)
    }
    upsertCrisisInfoApi.put(apiEndpoints.crisismanagement.upsertcrisisinfo, formikValues)
  }

  return (
    <>
      <Formik
        innerRef={formRef}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationStep}>
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          handleSubmit,
          setFieldValue
        }) => (
          <form onSubmit={handleSubmit} noValidate>
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={8}>
                  <PaperBox mt={0}>
                    <Box mb={3}>
                      <Typography fontWeight={600} variant="bodyMedium" color="textPrimary">
                        {t("global.company_information", "Company information")}
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={8}>
                        <TextField
                          autoComplete="off"
                          fullWidth
                          id="name"
                          label={t("global.name", "Name")}
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.name && errors.name ? true : false}
                          helperText={touched.name && errors.name ? errors.name : null}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          autoComplete="off"
                          fullWidth
                          id="organisationNumber"
                          label={t("settings.form.orgnr", "Org. no.")}
                          value={values.organisationNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.organisationNumber && errors.organisationNumber ? true : false}
                          helperText={
                            touched.organisationNumber && errors.organisationNumber
                              ? errors.organisationNumber
                              : null
                          }
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          autoComplete="off"
                          fullWidth
                          id="streetAddress"
                          label={t("global.street_address", "Street address")}
                          value={values.streetAddress}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.streetAddress && errors.streetAddress ? true : false}
                          helperText={
                            touched.streetAddress && errors.streetAddress ? errors.streetAddress : null
                          }
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          autoComplete="off"
                          fullWidth
                          id="postalCode"
                          label={t("settings.form.postalCode", "Postal code")}
                          value={values.postalCode}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.postalCode && errors.postalCode ? true : false}
                          helperText={touched.postalCode && errors.postalCode ? errors.postalCode : null}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <TextField
                          autoComplete="off"
                          fullWidth
                          id="cityAddress"
                          label={t("global.city", "City")}
                          value={values.cityAddress}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.cityAddress && errors.cityAddress ? true : false}
                          helperText={touched.cityAddress && errors.cityAddress ? errors.cityAddress : null}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid
                        item
                        md={12}
                        display="flex"
                        direction="row"
                        justifyContent={editMode ? "space-between" : "flex-end"}
                        mt={3}
                      >
                        {editMode && (
                          <Box>
                            <Button color="primary" variant="outlined" onClick={openRemoveDialog}>
                              {t("settings.form.button.remove_company", "Remove company")}
                            </Button>
                          </Box>
                        )}
                        <Box>
                          <Button
                            result={result}
                            color="primary"
                            onClick={() => {
                              if (formRef.current) {
                                formRef.current.submitForm()
                              }
                            }}>
                            {t("global.save", "Save")}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </PaperBox>
                </Grid>
                <Grid item xs={12} lg={4}>
                  {customerHasFeature(appContext?.appUser?.customer, allFeatures.VehicleRegistry) && (
                    <VtrSwitch
                      img="assets/Transportstyrelsen-icon.jpg"
                      id={`hasVtrIntegration`}
                      title={t("settings.form.vtr_title", "Swedish Transport Agency")}
                      message={t(
                        "settings.form.vtr_description",
                        "Your company has permission to import vehicles from the Swedish Transport Agency"
                      )}
                      value={values.hasVtrIntegration}
                      handleChange={() => {
                        setFieldValue(`hasVtrIntegration`, !values.hasVtrIntegration)
                      }}
                    />
                  )}
                  {customerHasFeature(appContext.appUser.customer, allFeatures.IssueReports) && (
                    <IssueReportNotification setFieldValue={setFieldValue} values={values} />
                  )}
                  {customerHasFeature(appContext?.appUser?.customer, allFeatures.Planning) && (
                    <PaperBox>
                      <BplIntegrationSwitch
                        t={t}
                        values={values}
                        setFieldValue={setFieldValue}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        setValidationStep={setValidationStep}
                      />
                    </PaperBox>
                  )}
                  {customerHasFeature(appContext?.appUser?.customer, allFeatures.Violation) && (
                    <PaperBox>
                      <ViolationSwitch
                        t={t}
                        values={values}
                        setFieldValue={setFieldValue}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        editCompany={company}
                        setValidationStep={setValidationStep}
                      />
                    </PaperBox>
                  )}
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <PaperBox mt={0}>
                    <Box mb={3}>
                      <Typography fontWeight={600} variant="bodyMedium" color="textPrimary">
                        {t("global.departments", "Departments")}
                      </Typography>
                    </Box>
                    <DepartmentForm
                      onSubmit={(item) => {
                        setDepartments((s) => {
                          return [
                            ...s,
                            {
                              ...item,
                              id: 0,
                              companyId: editMode ? company.id : 0
                            }
                          ]
                        })
                      }}
                    />
                    <DepartmentsList
                      departments={departments}
                      removeDepartment={(item) => {
                        setDepartments((s) => {
                          return s.filter((x) => x.listId !== item.listId)
                        })
                      }}
                    />
                    <Grid
                      container
                      direction="row"
                      justifyContent={editMode ? "space-between" : "flex-end"}
                      mt={3}
                    >
                      {editMode && (
                        <Box>
                          <Button color="primary" variant="outlined" onClick={openRemoveDialog}>
                            {t("deviation.form.button.remove_company", "Remove company")}
                          </Button>
                        </Box>
                      )}
                      <Box>
                        <Button type="submit" color="primary" result={result}>
                          {t("global.save", "Save")}
                        </Button>
                      </Box>
                    </Grid>
                  </PaperBox>
                </Grid>
              </Grid>
            </TabPanel>
            {workshopsResults && workshopsResults.status === 2 && (
              <TabPanel value={tabValue} index={3}>
                <PaperBox sx={{ padding: 0 }} mt={0}>
                  <ClpTable
                    columns={columns}
                    rows={workshopsResults.status === 2 && workshopsResults.data}
                    loadingState={workshopsResults.status}
                    initialFilters={workshopInitialFilters}
                  />
                </PaperBox>
              </TabPanel>
            )}
          </form>
        )}
      </Formik>
      <TabPanel value={tabValue} index={2}>
        {(getCrisisInfoResult.status === 2 || getCrisisInfoResult.status === 3) && (
          <CrisisInfoForm
            handleSubmit={handleCrisisSubmit}
            initialCrisisInfo={getCrisisInfoResult.data ? getCrisisInfoResult.data : null}
            result={upsertCrisisInfoResult}
            feedback={Feedback}
          />
        )}
      </TabPanel>
    </>
  )
}
